import React, { createRef, useRef, useState } from 'react';

import { useOnClickOutside } from '@flywire/react-hooks';
import classNames from 'classnames';

import './ToggleMenu.scss';

const ARROW_UP = 38;
const ARROW_DOWN = 40;
const ENTER = 13;
const ESCAPE = 27;
const TAB = 9;
const NAVIGATION_ARROW_KEYS = [ARROW_UP, ARROW_DOWN];

type MenuItem = {
  className?: string;
  label: string;
  path: string;
  onClick?: () => void;
};

type ToggleMenuProps = {
  label: string;
  menuItems: MenuItem[];
};

const ToggleMenu = ({ label, menuItems }: ToggleMenuProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedElement, setSelectedElement] = useState<number | null>(null);
  const ref = useRef<HTMLDivElement | null>(null);
  const itemsRef = useRef(menuItems.map(() => createRef<HTMLLIElement>()));

  useOnClickOutside(ref, () => {
    setIsOpen(false);
    selectElement(null);
  });

  const selectElement = async (element: number | null) => {
    if (element === null || isNaN(element)) {
      return;
    }

    await setSelectedElement(element);
    itemsRef.current[element].current?.focus();
  };

  const lastElementIndex = menuItems.length - 1;
  const noElementIsSelected = () => selectedElement === null;

  const handleButtonKeyDown = (e: React.KeyboardEvent) => {
    if (NAVIGATION_ARROW_KEYS.includes(e.keyCode)) {
      e.preventDefault();
    }
    if (e.keyCode === TAB) {
      setIsOpen(false);
    }

    if (isOpen) {
      return handleListKeyDown(e);
    }

    if (NAVIGATION_ARROW_KEYS.includes(e.keyCode)) {
      toggleMenuItems();
    }
    if (e.keyCode === ARROW_DOWN) {
      selectFirstElement();
    }
    if (e.keyCode === ARROW_UP) {
      selectLastElement();
    }
  };

  const handleListKeyDown = (e: React.KeyboardEvent) => {
    if (selectedElement) {
      const selectedMenuItem = menuItems[selectedElement];

      if (e.keyCode === ESCAPE) {
        toggleMenuItems();
      }
      if (e.keyCode === ENTER && selectedMenuItem) {
        selectedMenuItem.onClick?.();
      }
      if (e.keyCode === ARROW_DOWN) {
        return selectNextListElement();
      }
      if (e.keyCode === ARROW_UP) {
        return selectPreviousListElement();
      }
    }
  };

  const selectFirstElement = () => {
    const firstElement = 0;
    selectElement(firstElement);
  };

  const selectLastElement = () => {
    selectElement(lastElementIndex);
  };

  const selectNextListElement = () => {
    const isLastElementSelected = () => selectedElement === lastElementIndex;

    if (noElementIsSelected()) {
      return selectFirstElement();
    }
    if (isLastElementSelected()) {
      return selectFirstElement();
    }

    selectedElement && selectElement(selectedElement + 1);
  };

  const selectPreviousListElement = () => {
    const isFirstElementSelected = () => selectedElement === 0;

    if (noElementIsSelected()) {
      return selectLastElement();
    }
    if (isFirstElementSelected()) {
      return selectLastElement();
    }

    selectedElement && selectElement(selectedElement - 1);
  };

  const handleMenuItemClick = (onClick?: () => void) => {
    setIsOpen(false);
    onClick?.();
  };

  const toggleMenuItems = () => {
    setIsOpen(!isOpen);
    selectElement(null);
  };

  const withoutSpaces = (s: string) => s.split(' ').join('');

  const renderMenuItem = (option: MenuItem, index: number) => {
    const { className, label: itemLabel, path, onClick } = option;
    const isActive = selectedElement === index;
    const itemId = `${withoutSpaces(label)}-item-${index}`;

    return (
      <li
        onKeyDown={e => handleButtonKeyDown(e)}
        aria-label={itemLabel}
        aria-selected={isActive}
        ref={itemsRef.current[index]}
        id={itemId}
        className={classNames('ToggleMenu-menuItem', className, {
          'is-active': isActive,
        })}
        data-label={itemLabel}
        key={path}
        onClick={() => handleMenuItemClick(onClick)}
        tabIndex={-1}
      >
        {itemLabel}
      </li>
    );
  };

  const numberOfItems = menuItems.length;
  const statusText = isOpen
    ? `${numberOfItems} options are available, use up and down arrow keys to navigate. Press Enter key to select or Escape key to cancel.`
    : '';
  const selectedOptionId = `${withoutSpaces(label)}-item-${selectedElement}`;

  return (
    <div ref={ref} className={classNames('ToggleMenu', { 'is-open': isOpen })}>
      <button
        aria-expanded={isOpen}
        aria-label={label}
        className="ToggleMenu-label"
        onClick={() => toggleMenuItems()}
        onKeyDown={e => handleButtonKeyDown(e)}
        data-testid="my-account"
      >
        {label}
      </button>
      <ul
        tabIndex={-1}
        aria-activedescendant={selectedOptionId}
        className={classNames('ToggleMenu-menuItems', {
          'focus-within:outline focus-within:outline-2 active:outline-none':
            isOpen,
        })}
      >
        {menuItems.map(renderMenuItem)}
      </ul>
      <div className="ToggleMenu-status" role="status" aria-relevant="all">
        {statusText}
      </div>
    </div>
  );
};

export { ToggleMenu };
